import React, {
    createContext,
    useContext,
    useEffect,
    useRef,
    useState,
} from "react";

import SockJS from "sockjs-client";
import { Client } from "@stomp/stompjs";
import { getWorkspaceId } from "../Helper/workspaceStorage";
import SyncHelper from "../Helper/SyncHelper";
import { usePeerConnection } from "./PeerConnectionContext";

const WebSocketContext = createContext({
    isOnline: false,
    subscribe: () => () => { },
    publish: () => { },
});

export function WebSocketProvider({ children }) {
    const [isOnline, setIsOnline] = useState(false);

    const stompClientRef = useRef(null);
    const subscriptionsRef = useRef(new Map());
    const workspaceIdRef = useRef(null);

    const { getDataFromServer, sync } = SyncHelper();

    const { isConnected: isPeerConnected } = usePeerConnection();
    const isPeerSyncClient = isPeerConnected;

    const isPeerSyncClientRef = useRef(isPeerSyncClient);
    useEffect(() => {
        isPeerSyncClientRef.current = isPeerSyncClient;

        if (isPeerSyncClient) {
            setIsOnline(false);
        }
    }, [isPeerSyncClient]);

    /**
     * 🔥 WORKSPACE CHANGE DETECTION
     */
    const [workspaceState, setWorkspaceState] = useState(getWorkspaceId());

    /**
     * Sync wenn online
     */
    useEffect(() => {
        if (isOnline) {
            getDataFromServer();
        }
    }, [isOnline]);

    useEffect(() => {
        if (!isOnline) return;

        let intervalId = null;

        const startSyncLoop = () => {
            if (!isOnline) return;
            intervalId = setInterval(async () => {
                try {
                    await sync();
                } catch (err) {
                    console.error("sync error:", err);
                }
            }, 500); // half a second
        };

        startSyncLoop();

        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [isOnline, workspaceState]);


    useEffect(() => {
        const interval = setInterval(() => {
            const newWorkspaceId = getWorkspaceId();

            if (newWorkspaceId !== workspaceState) {
                console.log("🔄 Workspace changed:", newWorkspaceId);
                setWorkspaceState(newWorkspaceId);
            }
        }, 500);

        return () => clearInterval(interval);
    }, [workspaceState]);

    /**
     * 🔥 MAIN WEBSOCKET EFFECT (reconnect on workspace change)
     */
    useEffect(() => {
        if (!workspaceState) return;

        let isCancelled = false;

        const initWebSocket = () => {
            const mainURL =
                window?.RUNTIME_CONFIG?.BACKEND_URL ||
                "http://localhost:8080";

            if (stompClientRef.current) {
                console.log("🔌 Closing old WebSocket");
                stompClientRef.current.deactivate();
                stompClientRef.current = null;
            }

            subscriptionsRef.current.clear();

            workspaceIdRef.current = workspaceState;

            const stompClient = new Client({
                webSocketFactory: () =>
                    new SockJS(`${mainURL}/ws`),

                reconnectDelay: 2000,

                onConnect: () => {
                    if (isCancelled) return;
                    if (isPeerSyncClientRef.current) {
                        setIsOnline(false);
                        return;
                    }

                    setIsOnline(true);
                },

                onDisconnect: () => {
                    console.log("❌ WebSocket disconnected");
                    setIsOnline(false);
                },

                onWebSocketClose: () => {
                    console.log("🔌 WebSocket closed");
                    setIsOnline(false);
                },

                onStompError: (frame) => {
                    console.error("STOMP error:", frame);
                },
            });

            stompClient.activate();
            stompClientRef.current = stompClient;
        };

        initWebSocket();

        return () => {
            isCancelled = true;

            if (stompClientRef.current) {
                stompClientRef.current.deactivate();
                stompClientRef.current = null;
            }
        };
    }, [workspaceState]);

    /**
     * 📡 SUBSCRIBE
     */
    const subscribe = (destination, callback) => {
        if (isPeerSyncClient) {
            return () => { };
        }

        let entry = subscriptionsRef.current.get(destination);

        if (!entry) {
            entry = {
                callbacks: new Set(),
                subscription: null,
            };

            subscriptionsRef.current.set(destination, entry);

            const ws = stompClientRef.current;
            const workspaceId = workspaceIdRef.current;

            if (ws?.connected && workspaceId) {
                const topic = `/topic/${workspaceId}/${destination}`;
                entry.subscription = ws.subscribe(topic, (msg) => {
                    entry.callbacks.forEach((cb) => cb(msg));
                });
            }
        }

        entry.callbacks.add(callback);

        return () => {
            entry.callbacks.delete(callback);

            if (entry.callbacks.size === 0) {
                entry.subscription?.unsubscribe();
                subscriptionsRef.current.delete(destination);
            }
        };
    };

    /**
     * 📤 PUBLISH
     */
    const publish = (destination, body) => {
        const ws = stompClientRef.current;
        const workspaceId = workspaceIdRef.current;

        if (ws?.connected && workspaceId) {
            ws.publish({
                destination: `/topic/${workspaceId}/${destination}`,
                body:
                    typeof body === "string"
                        ? body
                        : JSON.stringify(body),
            });
        }
    };

    return (
        <WebSocketContext.Provider
            value={{ isOnline, subscribe, publish }}
        >
            {children}
        </WebSocketContext.Provider>
    );
}

export function useWebSocket() {
    return useContext(WebSocketContext);
}